import React, { useMemo } from "react";
import { ComponentConfig, WebConfig } from "types/index";

import { Container } from "@chakra-ui/react";
import { getComponentByType } from "helpers";
import { withoutDisabledFeatureLinks } from "utils/featureLinks";

export const getComponentToRender = (
  components: ComponentConfig[],
  baseKey: string,
  extraProps: Record<string, unknown> = {},
) => {
  return (
    components?.map((item, index) => {
      const elem = getComponentByType(item.component_type);
      const props = (extraProps && extraProps[item.component_type]) || {};

      if (!elem) return null;

      if (item.container_type === "padded") {
        return (
          <Container key={`${baseKey}__component_${index}`}>
            {React.createElement(elem, {
              ...item.props,
              ...props,
            })}
          </Container>
        );
      }
      return React.createElement(elem, {
        key: `${baseKey}__component_${index}`,
        ...item.props,
        ...props,
      });
    }) || []
  );
};

function useWebConfig({
  config,
  page_type,
}: {
  config: WebConfig | null;
  page_type: string | null;
}) {
  return useMemo(() => {
    // Nav, footer and body entries that lead to a page of a feature the
    // company has off (the "F&B" link, a food CTA) are dropped here, so every
    // surface built from the template stays free of dead links.
    const visible = <T,>(value: T): T =>
      withoutDisabledFeatureLinks(config?.features, value);
    const pageConfig =
      (page_type &&
        config?.pages?.find((page) => page.page_type === page_type)) ||
      null;
    return {
      pageConfig: visible(pageConfig),
      footerElements: getComponentToRender(
        visible(config?.template?.footer_components || []),
        "footer_component",
        visible(config?.template_props?.footer_components_props || {}),
      ),
      headerElements: getComponentToRender(
        visible(config?.template?.header_components || []),
        "header_component",
        visible(config?.template_props?.header_components_props || {}),
      ),
    };
  }, [config, page_type]);
}

export default useWebConfig;
